/**
 * Countdown timer configuration.
 *
 * @example
 * const config: TimerConfig = {
 *   expiryTime: '2026-07-21T18:30:00Z',
 * };
 */
export interface TimerConfig {
  /** Target date/time when the countdown ends. */
  expiryTime: string;
}

/**
 * Optional countdown lifecycle callbacks.
 */
export interface TimerCallbacks {
  /** Invoked every second with the latest timer state. */
  onTick?(state: TimerState): void;

  /** Invoked once when the countdown reaches zero. */
  onCompleted?(): void;
}

/**
 * Countdown timer lifecycle.
 */
export enum TimerStatus {
  /** Timer has not started. */
  IDLE = 'IDLE',

  /** Timer is actively counting down. */
  RUNNING = 'RUNNING',

  /** Timer has finished. */
  COMPLETED = 'COMPLETED',
}

/**
 * UI-friendly countdown values.
 */
export interface TimerDisplay {
  /** Time formatted as `MM:SS`. */
  formatted: string;

  /** Remaining minutes. */
  minutes: number;

  /** Remaining seconds (`0-59`). */
  seconds: number;
}

/**
 * Current countdown state.
 */
export interface TimerState {
  /** Current timer lifecycle status. */
  status: TimerStatus;

  /** Remaining time in milliseconds. */
  remainingTimeMs: number;

  /** Values ready for display. e.g. 05:30 */
  display: TimerDisplay;
}
